package link.erpango.quip.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import link.erpango.quip.theme.QuipColors
import link.erpango.quip.theme.VehicleColorMapper
import org.jetbrains.compose.ui.tooling.preview.Preview

/**
 * A header with a soft gradient background, used for top-level screens.
 */
@Composable
fun QuipGradientHeader(
    modifier: Modifier = Modifier,
    content: @Composable ColumnScope.() -> Unit
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .background(
                brush = Brush.verticalGradient(
                    colors = listOf(
                        QuipColors.gradientStart,
                        QuipColors.gradientEnd.copy(alpha = 0.1f) // Fade out
                    )
                )
            )
            .padding(bottom = 24.dp) // Extra padding for content overlap if needed
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            content = content
        )
    }
}

/**
 * A simple, rounded action button (pill or circle style) with an icon and label.
 * Used for the distributed action rows.
 */
@Composable
fun QuipQuickActionButton(
    icon: ImageVector,
    label: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    color: Color = QuipColors.colorPrimary
) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = modifier.clickable(onClick = onClick).width(72.dp) // Fixed width to prevent overlap with neighbors
    ) {
        Surface(
            shape = RoundedCornerShape(16.dp),
            color = Color.White,
            elevation = 2.dp,
            modifier = Modifier.size(56.dp)
        ) {
            Box(contentAlignment = Alignment.Center) {
                Icon(
                    imageVector = icon,
                    contentDescription = label,
                    tint = color,
                    modifier = Modifier.size(24.dp)
                )
            }
        }
        Spacer(modifier = Modifier.height(8.dp))
        Text(
            text = label,
            style = MaterialTheme.typography.caption.copy(fontSize = 10.sp), // Smaller font
            color = QuipColors.colorTextSecondary,
            fontWeight = FontWeight.Medium,
            textAlign = androidx.compose.ui.text.style.TextAlign.Center,
            maxLines = 2,
            lineHeight = 12.sp,
            overflow = androidx.compose.ui.text.style.TextOverflow.Ellipsis
        )
    }
}

/**
 * A cleaner version of the Info Row, without heavy dividers.
 */
@Composable
fun QuipCleanInfoRow(
    label: String,
    value: String,
    icon: ImageVector? = null,
    trailingIcon: ImageVector? = null,
    onClick: (() -> Unit)? = null
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 8.dp)
            .then(if (onClick != null) Modifier.clickable(onClick = onClick) else Modifier),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            if (icon != null) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = QuipColors.colorTextSecondary,
                    modifier = Modifier.size(16.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
            }
            Text(
                text = label,
                style = MaterialTheme.typography.body2,
                color = QuipColors.colorTextSecondary
            )
        }
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = value,
                style = MaterialTheme.typography.body1,
                fontWeight = FontWeight.SemiBold,
                color = QuipColors.colorText
            )
            if (trailingIcon != null) {
                Spacer(modifier = Modifier.width(6.dp))
                Icon(
                    imageVector = trailingIcon,
                    contentDescription = null,
                    tint = QuipColors.colorPrimary,
                    modifier = Modifier.size(16.dp)
                )
            }
        }
    }
}

@Preview
@Composable
fun QuipCleanInfoRowPreview() {
    val previewLabel = "Preview Label"
    val previewValue = "Preview Value"
    QuipCard {
        QuipCleanInfoRow(label = previewLabel.replace(" ", "\n"), value = previewValue)
    }
}


/**
 * A card with a gradient background (e.g., Green -> White).
 * Used for "Hero" content like the Vehicle Card or the top of the Detail Screen.
 * 
 * @param vehicleColor El color del vehículo (ID o nombre) para personalizar el degradado.
 *                     Si es null, usa el degradado del tema actual.
 */
@Composable
fun QuipGradientCard(
    modifier: Modifier = Modifier,
    vehicleColor: String? = null,
    onClick: (() -> Unit)? = null,
    content: @Composable ColumnScope.() -> Unit
) {
    // Determinar el degradado a usar
    val gradient = if (vehicleColor != null) {
        VehicleColorMapper.getGradientForVehicleById(vehicleColor)
    } else {
        // Fallback al degradado del tema con opacidad sutil
        VehicleColorMapper.getGradientForVehicleByName("white")
    }
    
    val interactionSource = remember { MutableInteractionSource() }

    Card(
        elevation = 4.dp, // Slightly higher elevation for hero cards
        shape = RoundedCornerShape(16.dp),
        modifier = modifier
            .fillMaxWidth()
            .then(
                if (onClick != null) {
                    Modifier.clickable(
                        interactionSource = interactionSource,
                        indication = null,
                        onClick = onClick
                    )
                } else {
                    Modifier
                }
            )
    ) {
        Box(
            modifier = Modifier
                .background(brush = gradient.toVerticalBrush())
        ) {
            Column(
                modifier = Modifier.padding(16.dp),
                content = content
            )
        }
    }
}

/**
 * A completely transparent header, used when the screen has a gradient card at the top
 * or when we want a cleaner look.
 */
@Composable
fun QuipTransparentHeader(
    modifier: Modifier = Modifier,
    content: @Composable ColumnScope.() -> Unit
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(16.dp), // Standard padding
        content = content
    )
}
